import React, { useEffect, useState } from "react";
import { Clock, Sparkle, ChartLineUp, User, PaperPlaneTilt, Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

function ScoreTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload;
  return (
    <div className="bg-zinc-950 border border-orange-500/40 rounded-sm px-2.5 py-1.5 text-xs font-mono">
      <div className="text-orange-400">{new Date(p.snapshot_at || label).toLocaleDateString(undefined, { month: "short", day: "numeric" })}</div>
      <div className="text-zinc-100">score: {p.score}</div>
    </div>
  );
}

function ScoreChart({ data }) {
  if (!data || data.length < 2) {
    return <div className="text-[10px] font-mono text-zinc-600 px-2 py-6 text-center">Not enough data yet · snapshots run nightly at 2 AM</div>;
  }
  const first = data[0].score;
  const last = data[data.length - 1].score;
  const diff = last - first;
  const diffCls = diff > 0 ? "text-emerald-400" : diff < 0 ? "text-red-400" : "text-zinc-400";
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <div className="flex items-baseline gap-2">
          <span className="font-display text-2xl font-semibold text-zinc-100" data-testid="score-current">{last}</span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">current score</span>
        </div>
        <div className={`font-mono text-xs ${diffCls}`} data-testid="score-diff">
          {diff > 0 ? "+" : ""}{diff} pts
        </div>
      </div>
      <ResponsiveContainer width="100%" height={140}>
        <AreaChart data={data} margin={{ top: 5, right: 8, bottom: 5, left: -20 }}>
          <defs>
            <linearGradient id="score-gradient" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#ea580c" stopOpacity={0.5} />
              <stop offset="100%" stopColor="#ea580c" stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
          <XAxis
            dataKey="snapshot_at"
            tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: "numeric", day: "numeric" })}
            tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
            stroke="#3f3f46"
            axisLine={false}
            tickLine={false}
            minTickGap={30}
          />
          <YAxis
            domain={[0, 100]}
            tick={{ fill: "#71717a", fontSize: 10, fontFamily: "JetBrains Mono, monospace" }}
            stroke="#3f3f46"
            axisLine={false}
            tickLine={false}
            width={30}
          />
          <Tooltip content={<ScoreTooltip />} />
          <Area
            type="monotone"
            dataKey="score"
            stroke="#ea580c"
            strokeWidth={2}
            fill="url(#score-gradient)"
            activeDot={{ r: 4, fill: "#ea580c", stroke: "#fff", strokeWidth: 1 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function FieldChange({ change }) {
  return (
    <div className="text-[11px] font-mono">
      <span className="text-zinc-500">{change.field}:</span>{" "}
      <span className="text-red-400/80 line-through">{String(change.old) || "—"}</span>
      <span className="text-zinc-600 mx-1">→</span>
      <span className="text-emerald-400">{String(change.new) || "—"}</span>
    </div>
  );
}

function sourceLabel(s) {
  const m = {
    user: { label: "User", color: "text-zinc-300" },
    resend_webhook: { label: "Email click", color: "text-amber-400" },
    resend_inbound_reply: { label: "Inbound reply", color: "text-emerald-400" },
    sentiment_triage: { label: "AI triage", color: "text-orange-400" },
    stripe_webhook: { label: "Stripe payment", color: "text-emerald-400" },
    pagespeed: { label: "PageSpeed", color: "text-blue-400" },
    reviews: { label: "Reviews", color: "text-yellow-400" },
    scheduler: { label: "Auto-cron", color: "text-zinc-400" },
    system: { label: "System", color: "text-zinc-400" },
  };
  return m[s] || { label: s, color: "text-zinc-400" };
}

export default function HistoryTab({ lead, onLeadUpdated }) {
  const [audit, setAudit] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState("");
  const [triaging, setTriaging] = useState(false);
  const [days, setDays] = useState(30);

  const reload = async () => {
    if (!lead?.id) return;
    setLoading(true);
    try {
      const [a, h] = await Promise.all([
        api.get(`/leads/${lead.id}/audit`).catch(() => ({ data: [] })),
        api.get(`/leads/${lead.id}/score-history?days=${days}`).catch(() => ({ data: [] })),
      ]);
      setAudit(a.data);
      setHistory(h.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead?.id, days]);

  const triageReply = async () => {
    if (!replyText.trim() || !lead?.id) return;
    setTriaging(true);
    try {
      const { data } = await api.post("/sentiment/triage", {
        lead_id: lead.id,
        body_text: replyText.trim(),
      });
      if (data.skipped) {
        toast.message("Triage skipped", { description: data.reason || "Classifier unavailable" });
      } else {
        toast.success(`Classified ${data.sentiment?.toUpperCase()}`, {
          description: data.routed_stage ? `→ ${data.routed_stage}` : data.reason,
        });
      }
      setReplyText("");
      // Refetch lead so sentiment chip + score reflect new state
      try {
        const { data: fresh } = await api.get(`/leads/${lead.id}`);
        onLeadUpdated?.(fresh);
      } catch { /* non-fatal */ }
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Triage failed");
    } finally {
      setTriaging(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Score trend */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ChartLineUp size={16} weight="duotone" className="text-orange-500" />
            <div className="font-display font-medium text-zinc-100 text-sm">Lead score trend</div>
          </div>
          <div className="flex bg-zinc-950 border border-zinc-800 rounded-sm p-0.5" data-testid="score-range-toggle">
            {[30, 60, 90].map((d) => (
              <button
                key={d}
                data-testid={`score-range-${d}`}
                onClick={() => setDays(d)}
                className={`text-[10px] font-mono px-2 py-1 rounded-sm transition-colors ${
                  days === d ? "bg-orange-600 text-zinc-950 font-semibold" : "text-zinc-500 hover:text-zinc-200"
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <ScoreChart data={history} />
      </div>

      {/* Manual triage paste-box */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4" data-testid="manual-triage-card">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={16} weight="duotone" className="text-orange-500" />
          <div className="font-display font-medium text-zinc-100 text-sm">Triage a reply</div>
        </div>
        <div className="text-[11px] text-zinc-500 mb-2">
          Paste any reply (Gmail, LinkedIn DM, SMS) — Claude classifies it and auto-advances the stage.
        </div>
        <Textarea
          data-testid="manual-triage-input"
          value={replyText}
          onChange={(e) => setReplyText(e.target.value)}
          placeholder="e.g. 'Yes, lets chat — call me tomorrow at 605-555-0001'"
          className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs font-mono min-h-[70px] mb-2"
          disabled={triaging}
        />
        <div className="flex justify-end">
          <Button
            size="sm"
            data-testid="manual-triage-btn"
            onClick={triageReply}
            disabled={triaging || !replyText.trim()}
            className="bg-orange-600 hover:bg-orange-500 text-zinc-950 rounded-sm"
          >
            {triaging ? (
              <><Spinner size={12} className="mr-1.5 animate-spin" /> Triaging…</>
            ) : (
              <><PaperPlaneTilt size={12} className="mr-1.5" /> Triage reply</>
            )}
          </Button>
        </div>
      </div>

      {/* Sentiment chip */}
      {lead?.sentiment_last && (
        <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-4" data-testid="sentiment-summary">
          <div className="flex items-center gap-2 mb-2">
            <Sparkle size={16} weight="duotone" className="text-orange-500" />
            <div className="font-display font-medium text-zinc-100 text-sm">Latest reply triage</div>
          </div>
          <div className="flex items-center gap-2 mb-1">
            <span
              data-testid="sentiment-badge"
              className={`stage-pill px-1.5 py-0.5 rounded-sm border ${
                lead.sentiment_last === "hot"
                  ? "border-red-500/40 text-red-300 bg-red-500/10"
                  : lead.sentiment_last === "warm"
                  ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                  : lead.sentiment_last === "objection"
                  ? "border-blue-500/40 text-blue-300 bg-blue-500/10"
                  : "border-zinc-700 text-zinc-400 bg-zinc-900"
              }`}
            >
              {lead.sentiment_last.toUpperCase()}
            </span>
            <span className="text-xs text-zinc-500 font-mono">
              {lead.sentiment_last_at ? new Date(lead.sentiment_last_at).toLocaleString() : ""}
            </span>
          </div>
          {lead.sentiment_last_reason && <div className="text-xs text-zinc-300 mt-1">{lead.sentiment_last_reason}</div>}
          {lead.sentiment_last_action && <div className="text-xs text-orange-300 mt-1">→ {lead.sentiment_last_action}</div>}
        </div>
      )}

      {/* Audit timeline */}
      <div className="bg-zinc-900/60 border border-zinc-800 rounded-sm">
        <div className="px-4 py-2.5 border-b border-zinc-800 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Clock size={14} className="text-zinc-500" />
            <div className="font-display font-medium text-zinc-100 text-sm">Audit log</div>
          </div>
          <div className="font-mono text-[10px] text-zinc-500" data-testid="audit-count">{audit.length} events</div>
        </div>
        {loading ? (
          <div className="px-4 py-6 text-center text-xs text-zinc-500">Loading…</div>
        ) : audit.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-zinc-500" data-testid="audit-empty">No audit events yet.</div>
        ) : (
          <div className="divide-y divide-zinc-800/60 max-h-[400px] overflow-y-auto">
            {audit.map((e) => {
              const src = sourceLabel(e.source);
              return (
                <div key={e.id} data-testid={`audit-row-${e.id}`} className="px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex items-center gap-1.5 text-[11px]">
                      <User size={12} className="text-zinc-500" />
                      <span className="text-zinc-300">{e.user_email || "system"}</span>
                      <span className={`font-mono text-[10px] uppercase tracking-wider ${src.color}`}>· {src.label}</span>
                      <span className="font-mono text-[10px] text-zinc-600 uppercase">· {e.action}</span>
                    </div>
                    <span className="font-mono text-[10px] text-zinc-600">{new Date(e.created_at).toLocaleString()}</span>
                  </div>
                  {(e.field_changes || []).map((c, i) => <FieldChange key={i} change={c} />)}
                  {e.note && <div className="text-[11px] text-zinc-400 italic mt-1">{e.note}</div>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
